import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import {
  CheckCircle, XCircle, Clock, FileText,
  Search, Eye, Ticket, RefreshCw, ChevronLeft, ChevronRight,
  Filter, ChevronDown, X,
} from 'lucide-react';
import { ticketService, isTicketApiSuccess } from '../../api/ticketService';
import inquiryCategoryMasterService from '../../api/inquiryCategoryMasterService';
import { extractErrorMessage } from '../../utils/errorHandler';
import toast from 'react-hot-toast';

const PAGE_SIZE = 10;

const EMPTY_PAGINATION = {
  pageNumber: 0,
  pageSize: PAGE_SIZE,
  totalElements: 0,
  totalPages: 0,
  first: true,
  last: true,
};

function normalizePaginatedResponse(data) {
  if (!data) {
    return { content: [], ...EMPTY_PAGINATION };
  }
  const content = Array.isArray(data.content)
    ? data.content
    : (Array.isArray(data) ? data : []);
  return {
    content,
    pageNumber: data.pageNumber ?? data.number ?? 0,
    pageSize: data.pageSize ?? data.size ?? PAGE_SIZE,
    totalElements: data.totalElements ?? content.length,
    totalPages: data.totalPages ?? (content.length > 0 ? 1 : 0),
    first: data.first ?? true,
    last: data.last ?? true,
  };
}

function hasActiveFilters(filters, tab) {
  const base = filters.priorityFilter !== 'all' || filters.categoryFilter !== 'all';
  if (tab === 'history') {
    return base
      || filters.changeTypeFilter !== 'all'
      || Boolean(filters.startDateFilter)
      || Boolean(filters.endDateFilter);
  }
  return base;
}

/**
 * Phase 6 — Supervisor Approval queue for resolved Inquiries.
 * Reuses /agent/approvals route; list UI aligned with Agent My Tickets table.
 */
const Approvals = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [pendingPagination, setPendingPagination] = useState(EMPTY_PAGINATION);
  const [historyPagination, setHistoryPagination] = useState(EMPTY_PAGINATION);
  const [pendingTotalCount, setPendingTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [changeTypeFilter, setChangeTypeFilter] = useState('all');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const [pendingPage, setPendingPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [priorityRows, categoryResp] = await Promise.all([
          ticketService.getInquiryPriorityLevelsForCreate(),
          inquiryCategoryMasterService.getAll(true),
        ]);
        if (cancelled) return;
        setInquiryPriorityLevels(Array.isArray(priorityRows) ? priorityRows : []);
        const catRows = categoryResp?.data?.data ?? categoryResp?.data ?? categoryResp;
        setInquiryCategories(Array.isArray(catRows) ? catRows : []);
      } catch (error) {
        console.warn('Failed to load inquiry masters for Approvals filters', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const buildQueryParams = useCallback((page, tab) => {
    const params = { page, size: PAGE_SIZE };
    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }
    if (priorityFilter !== 'all') {
      params.priorityId = priorityFilter;
    }
    if (categoryFilter !== 'all') {
      params.categoryId = categoryFilter;
    }
    if (tab === 'history') {
      if (changeTypeFilter !== 'all') {
        params.changeType = changeTypeFilter;
      }
      if (startDateFilter) {
        params.startDate = `${startDateFilter}T00:00:00`;
      }
      if (endDateFilter) {
        params.endDate = `${endDateFilter}T23:59:59`;
      }
    }
    return params;
  }, [
    debouncedSearch, priorityFilter, categoryFilter,
    changeTypeFilter, startDateFilter, endDateFilter,
  ]);

  const resetActivePage = useCallback(() => {
    if (activeTab === 'pending') {
      setPendingPage(0);
    } else {
      setHistoryPage(0);
    }
  }, [activeTab]);

  const filtersActive = useMemo(() => hasActiveFilters({
    priorityFilter,
    categoryFilter,
    changeTypeFilter,
    startDateFilter,
    endDateFilter,
  }, activeTab), [priorityFilter, categoryFilter, changeTypeFilter, startDateFilter, endDateFilter, activeTab]);

  const hasSearchOrFilters = Boolean(debouncedSearch.trim()) || filtersActive;

  const mapPendingTicket = (ticket) => {
    const priority =
      ticket.inquiryPriorityLevelName
      || ticket.priorityName
      || ticket.priority
      || '—';
    return {
      id: ticket.ticketId || ticket.id,
      ticketId: ticket.ticketId || ticket.id,
      ticketNumber: ticket.ticketNumber,
      subject: ticket.subject || 'Inquiry',
      title: ticket.ticketNumber
        ? `${ticket.ticketNumber} — ${ticket.subject || 'Inquiry'}`
        : (ticket.subject || 'Inquiry'),
      description: ticket.subject || '',
      requester: ticket.requesterFullName || ticket.requesterName || ticket.requesterEmail || '—',
      assignee: ticket.assigneeFullName || ticket.assigneeName || '—',
      priority: String(priority).toLowerCase(),
      priorityLabel: priority,
      type: 'ticket',
      status: 'pending',
      requestedAt: ticket.resolvedAt || ticket.updatedAt || ticket.createdAt,
      statusName: ticket.statusName || ticket.status || 'Pending Supervisor Approval',
    };
  };

  const mapHistoryRow = (row) => ({
    id: row.historyId || `${row.ticketId}-${row.changeType}-${row.createdAt}`,
    ticketId: row.ticketId,
    ticketNumber: row.ticketNumber,
    subject: row.subject || 'Inquiry',
    title: row.ticketNumber
      ? `${row.ticketNumber} — ${row.subject || 'Inquiry'}`
      : (row.subject || 'Inquiry'),
    description: row.changeDescription || '',
    requester: '—',
    priority: '—',
    priorityLabel: '—',
    type: 'ticket',
    status: row.changeType === 'supervisor_approved' ? 'approved' : 'rejected',
    statusLabel: row.changeType === 'supervisor_approved' ? 'Approved' : 'Rejected',
    requestedAt: row.createdAt,
    comment: row.changeDescription || '',
  });

  const loadPending = useCallback(async (params) => {
    const data = await ticketService.getPendingSupervisorApprovals(params);
    const normalized = normalizePaginatedResponse(data);
    setPendingApprovals(normalized.content.map(mapPendingTicket));
    setPendingPagination(normalized);
    setPendingTotalCount(normalized.totalElements);
    return normalized;
  }, []);

  const loadHistory = useCallback(async (params) => {
    const data = await ticketService.getSupervisorApprovalHistory(params);
    const normalized = normalizePaginatedResponse(data);
    setApprovalHistory(normalized.content.map(mapHistoryRow));
    setHistoryPagination(normalized);
    return normalized;
  }, []);

  const refreshPendingCount = useCallback(async () => {
    try {
      const data = await ticketService.getPendingSupervisorApprovals({ page: 0, size: 1 });
      const normalized = normalizePaginatedResponse(data);
      setPendingTotalCount(normalized.totalElements);
    } catch (error) {
      console.error('Failed to refresh pending approval count:', error);
    }
  }, []);

  const runTabFetch = useCallback(async (tab, page) => {
    const params = buildQueryParams(page, tab);
    if (tab === 'pending') {
      await loadPending(params);
      return;
    }
    await loadHistory(params);
    await refreshPendingCount();
  }, [buildQueryParams, loadPending, loadHistory, refreshPendingCount]);

  const fetchActiveTabData = useCallback(async (tab, page) => {
    setLoading(true);
    try {
      await runTabFetch(tab, page);
    } catch (error) {
      console.error('Failed to load supervisor approvals:', error);
      toast.error(extractErrorMessage(error, 'Failed to load approvals'));
      if (tab === 'pending') {
        setPendingApprovals([]);
        setPendingPagination(EMPTY_PAGINATION);
        setPendingTotalCount(0);
      } else {
        setApprovalHistory([]);
        setHistoryPagination(EMPTY_PAGINATION);
      }
    } finally {
      setLoading(false);
    }
  }, [runTabFetch]);

  // Debounced server-side search (My Tickets pattern: 500ms typing, 300ms clear).
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, searchTerm.trim() ? 500 : 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const activePage = activeTab === 'pending' ? pendingPage : historyPage;
    if (activePage !== 0) {
      resetActivePage();
    }
  }, [debouncedSearch, priorityFilter, categoryFilter, changeTypeFilter, startDateFilter, endDateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single fetch driver: active tab + page + search/filters + route re-entry.
  useEffect(() => {
    let cancelled = false;
    const page = activeTab === 'pending' ? pendingPage : historyPage;
    const isSearchFetch = debouncedSearch.trim().length > 0;

    (async () => {
      if (isSearchFetch) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      try {
        await runTabFetch(activeTab, page);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load supervisor approvals:', error);
        toast.error(extractErrorMessage(error, 'Failed to load approvals'));
        if (activeTab === 'pending') {
          setPendingApprovals([]);
          setPendingPagination(EMPTY_PAGINATION);
          setPendingTotalCount(0);
        } else {
          setApprovalHistory([]);
          setHistoryPagination(EMPTY_PAGINATION);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSearchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab, pendingPage, historyPage, location.key,
    debouncedSearch, priorityFilter, categoryFilter,
    changeTypeFilter, startDateFilter, endDateFilter,
    runTabFetch,
  ]);

  const handleApprove = async (ticketId) => {
    if (!ticketId || submitting) return;
    setSubmitting(true);
    try {
      const response = await ticketService.approveSupervisorResolution(ticketId);
      if (!isTicketApiSuccess(response)) {
        toast.error(response?.data?.message || 'Failed to approve inquiry');
        return;
      }
      toast.success('Inquiry resolution approved');
      setShowDetailsModal(false);
      setApprovalComment('');
      setSelectedApproval(null);
      await fetchActiveTabData(activeTab, activeTab === 'pending' ? pendingPage : historyPage);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to approve inquiry'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (ticketId, comment = '') => {
    if (!ticketId || submitting) return;
    if (!comment.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setSubmitting(true);
    try {
      const response = await ticketService.rejectSupervisorResolution(ticketId, comment.trim());
      if (!isTicketApiSuccess(response)) {
        toast.error(response?.data?.message || 'Failed to reject inquiry');
        return;
      }
      toast.success('Inquiry resolution rejected — returned to Responsible User');
      setShowDetailsModal(false);
      setApprovalComment('');
      setSelectedApproval(null);
      await fetchActiveTabData(activeTab, activeTab === 'pending' ? pendingPage : historyPage);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to reject inquiry'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (approval) => {
    setSelectedApproval(approval);
    setShowDetailsModal(true);
  };

  const openTicketDetail = (ticketId) => {
    if (!ticketId) return;
    navigate(`/agent/tickets/${ticketId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900';
      case 'approved': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'rejected': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'urgent': return 'text-red-50 dark:text-red-100 bg-red-800 dark:bg-red-950';
      case 'high': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getPriorityBorderColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'border-l-4 border-green-400';
      case 'medium': return 'border-l-4 border-yellow-400';
      case 'high': return 'border-l-4 border-orange-400';
      case 'urgent': return 'border-l-4 border-red-700 dark:border-red-600';
      case 'critical': return 'border-l-4 border-red-400';
      default: return 'border-l-4 border-gray-300 dark:border-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRefresh = () => {
    const page = activeTab === 'pending' ? pendingPage : historyPage;
    fetchActiveTabData(activeTab, page);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setPriorityFilter('all');
    setCategoryFilter('all');
    setChangeTypeFilter('all');
    setStartDateFilter('');
    setEndDateFilter('');
    toast.success('All filters cleared');
  };

  const handleSelectPendingTab = () => {
    if (activeTab !== 'pending') {
      setActiveTab('pending');
    }
  };

  const handleSelectHistoryTab = () => {
    if (activeTab !== 'history') {
      setActiveTab('history');
      setHistoryPage(0);
    }
  };

  const activePagination = activeTab === 'pending' ? pendingPagination : historyPagination;
  const activePage = activeTab === 'pending' ? pendingPage : historyPage;
  const setActivePage = activeTab === 'pending' ? setPendingPage : setHistoryPage;
  const displayRows = activeTab === 'pending' ? pendingApprovals : approvalHistory;

  const renderPagination = () => {
    const totalPages = activePagination.totalPages ?? 0;
    const totalElements = activePagination.totalElements ?? 0;
    if (totalPages <= 1 && totalElements <= PAGE_SIZE) return null;

    const safePage = Math.min(activePage, Math.max(totalPages - 1, 0));
    const showingFrom = totalElements === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const showingTo = Math.min((safePage + 1) * PAGE_SIZE, totalElements);

    return (
      <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Showing <span className="font-medium">{showingFrom}</span> to{' '}
            <span className="font-medium">{showingTo}</span> of{' '}
            <span className="font-medium">{totalElements}</span> results
          </p>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setActivePage((prev) => Math.max(prev - 1, 0))}
              disabled={activePagination.first || safePage === 0}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i;
              } else if (safePage < 3) {
                pageNum = i;
              } else if (safePage > totalPages - 4) {
                pageNum = totalPages - 5 + i;
              } else {
                pageNum = safePage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setActivePage(pageNum)}
                  className={`px-3 py-1 text-sm border rounded-md ${pageNum === safePage
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setActivePage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={activePagination.last || safePage >= totalPages - 1}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPendingTable = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
      {searchLoading && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 z-10">
          <div className="h-full bg-blue-600 animate-pulse" style={{ width: '30%' }} />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">S.No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ticket</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Requester</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Responsible User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Submitted</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {displayRows.map((approval, index) => (
              <tr
                key={approval.id}
                className={`bg-white dark:bg-gray-800 ${getPriorityBorderColor(approval.priority)} hover:shadow-md transition-all`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {activePage * PAGE_SIZE + index + 1}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <Ticket className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      </div>
                    </div>
                    <div className="ml-4 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {approval.ticketNumber || approval.ticketId}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {approval.subject}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-gray-100 truncate max-w-[10rem]" title={approval.requester}>
                    {approval.requester}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-gray-100 truncate max-w-[10rem]" title={approval.assignee}>
                    {approval.assignee}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(approval.priority)}`}>
                    {String(approval.priorityLabel || approval.priority).toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(approval.status)}`}>
                    {approval.statusName}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">{formatDate(approval.requestedAt)}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{formatTime(approval.requestedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={() => openTicketDetail(approval.ticketId)}
                  >
                    <Eye className="w-4 h-4" />
                    Review
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderPagination()}
    </div>
  );

  const renderHistoryTable = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
      {searchLoading && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 z-10">
          <div className="h-full bg-blue-600 animate-pulse" style={{ width: '30%' }} />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">S.No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ticket</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Outcome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {displayRows.map((approval, index) => (
              <tr
                key={approval.id}
                className="bg-white dark:bg-gray-800 border-l-4 border-gray-300 dark:border-gray-600 hover:shadow-md transition-all"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {activePage * PAGE_SIZE + index + 1}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <Ticket className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      </div>
                    </div>
                    <div className="ml-4 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {approval.ticketNumber || approval.ticketId}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                        {approval.subject}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-md" title={approval.description}>
                    {approval.description || '—'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(approval.status)}`}>
                    {approval.statusLabel || approval.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-gray-100">{formatDate(approval.requestedAt)}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{formatTime(approval.requestedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {approval.ticketId && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-2"
                      onClick={() => openTicketDetail(approval.ticketId)}
                    >
                      <Eye className="w-4 h-4" />
                      Open
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {renderPagination()}
    </div>
  );

  const renderEmptyState = (pending) => (
    <Card>
      <CardContent className="p-12 text-center">
        {pending ? (
          <>
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Pending Approvals
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              There are no resolved Inquiries waiting for Supervisor Approval.
            </p>
          </>
        ) : (
          <>
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Approval History
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Approved and rejected Inquiry decisions will appear here.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Approvals</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Review resolved Inquiries awaiting Supervisor Approval
          </p>
        </div>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex space-x-8">
          <button
            type="button"
            onClick={handleSelectPendingTab}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'pending'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending Approvals
              {pendingTotalCount > 0 && (
                <Badge className="bg-yellow-500 text-white">{pendingTotalCount}</Badge>
              )}
            </div>
          </button>
          <button
            type="button"
            onClick={handleSelectHistoryTab}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'history'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Approval History
            </div>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by ticket number or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${filtersActive ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <Filter className="w-4 h-4" />
          Filters
          {filtersActive && (
            <Badge className="bg-blue-600 text-white ml-1">!</Badge>
          )}
        </Button>
      </div>

      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-shrink-0">
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="all">All Inquiry Priorities</option>
                  {inquiryPriorityLevels.map((priority) => (
                    <option
                      key={priority.id || priority.inquiryPriorityLevelId}
                      value={String(priority.id || priority.inquiryPriorityLevelId)}
                    >
                      {priority.priorityLevelName || priority.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              <div className="relative flex-shrink-0">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="all">All Inquiry Categories</option>
                  {inquiryCategories.map((category) => {
                    const id = category.id || category.inquiryCategoryId;
                    return (
                      <option key={id} value={String(id)}>
                        {category.inquiryCategoryName || category.name || `Category ${id}`}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {activeTab === 'history' && (
                <>
                  <div className="relative flex-shrink-0">
                    <select
                      value={changeTypeFilter}
                      onChange={(e) => setChangeTypeFilter(e.target.value)}
                      className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                    >
                      <option value="all">All Outcomes</option>
                      <option value="supervisor_approved">Approved</option>
                      <option value="supervisor_rejected">Rejected</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  <input
                    type="date"
                    value={startDateFilter}
                    onChange={(e) => setStartDateFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    title="From date"
                  />
                  <input
                    type="date"
                    value={endDateFilter}
                    onChange={(e) => setEndDateFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    title="To date"
                  />
                </>
              )}

              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Clear All
                </Button>
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading approvals...</p>
          </CardContent>
        </Card>
      )}

      {!loading && activeTab === 'pending' && (
        (activePagination.totalElements === 0 && !hasSearchOrFilters)
          ? renderEmptyState(true)
          : (displayRows.length === 0 ? renderEmptyState(true) : renderPendingTable())
      )}

      {!loading && activeTab === 'history' && (
        (activePagination.totalElements === 0 && !hasSearchOrFilters)
          ? renderEmptyState(false)
          : (displayRows.length === 0 ? renderEmptyState(false) : renderHistoryTable())
      )}

      {showDetailsModal && selectedApproval && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {selectedApproval.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  {selectedApproval.description}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openTicketDetail(selectedApproval.ticketId)}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Open Ticket Detail
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Requester</label>
                  <p className="text-gray-900 dark:text-gray-100">{selectedApproval.requester}</p>
                </div>
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Priority</label>
                  <Badge className={getPriorityColor(selectedApproval.priority)}>
                    {selectedApproval.priorityLabel}
                  </Badge>
                </div>
                <div>
                  <label className="font-medium text-gray-700 dark:text-gray-300">Responsible User</label>
                  <p className="text-gray-900 dark:text-gray-100">{selectedApproval.assignee}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Remarks (required for rejection)
                </label>
                <Textarea
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  placeholder="Add rejection remarks..."
                  rows={3}
                  disabled={submitting}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  disabled={submitting}
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedApproval(null);
                    setApprovalComment('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={submitting}
                  onClick={() => handleApprove(selectedApproval.ticketId)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={submitting}
                  onClick={() => handleReject(selectedApproval.ticketId, approvalComment)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Approvals;
